import { Severity } from 'Data/ViewModels/Core/types';
import { FormatSpecifier, getTimeInputDataForPattern } from 'Lib/Format';
import classNames from 'classnames';
import moment from 'moment';
import React, { useState } from 'react';
import { withInjectedProps } from '../InjectedPropsContext';

// This is a wrapper around the datetime-local input type.
// https://developer.mozilla.org/en-US/docs/Web/HTML/Element/input/datetime-local
//
// Values are formatted as: YYYY-MM-DDTHH:mm[:ss[.SSS]]
//
// e.g.
// *  "2023-04-15T16:22"
// *  "2023-04-15T16:22:45"
// *  "2023-04-15T16:22:45.392"
//
// The pattern property of the format specifier is used to control whether or not the value includes
// seconds and/or milliseconds, and the `step` attribute for the underlying input.
//
// To include seconds in the input, specify a pattern that includes a second component ('s' or 'ss').
// e.g. "HH:mm:ss"
//
// To include both seconds and milliseconds in the input, specify a pattern that includes a fractional
// seconds component ('SSS'). e.g. "HH:mm:ss.SSS"
//

const minValidDate = '1800-01-01T00:00:00.000';
const maxValidDate = '9999-12-31T23:59:59.999';

export type IDateTimeInputProps = Pick<
  React.DetailedHTMLProps<React.InputHTMLAttributes<HTMLInputElement>, HTMLInputElement>,
  'defaultValue' | 'disabled' | 'id' | 'name' | 'onChange' | 'onKeyDown'
> & {
  focusRef?: React.Ref<HTMLInputElement>;
  severity?: Severity;
  value?: any;
  required?: boolean;
  format?: FormatSpecifier;
};

const DateTimeInputBase: React.FC<IDateTimeInputProps> = ({
  defaultValue,
  disabled,
  id,
  focusRef,
  name,
  onChange,
  onKeyDown,
  severity,
  value,
  required,
  format,
}) => {
  //Local state to prevent writing back invalid date/time value to the underlying model
  const [inputValue, setInputValue] = useState<any>();
  const [dirty, setDirty] = useState<boolean>(false);

  const formatInfo = getTimeInputDataForPattern(format?.pattern ?? moment.HTML5_FMT.TIME);

  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter' && !event.defaultPrevented) {
      invokeOnChange(event, true);
    }
    onKeyDown?.(event);
  };
  const handleBlur = (event: React.FocusEvent<HTMLInputElement>) => invokeOnChange(event, true);
  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => invokeOnChange(event, false);

  const invokeOnChange = (event: any, submitValues: boolean) => {
    let date = event.target.value?.length > 0 ? moment(event.target.value) : null;

    // In some browsers (Firefox), clicking the clear 'x' doesn't focus or blur the input, so
    // if the value was cleared, and we don't currently have focus on the input, submit the
    // change immediately.
    const submitValuesBecauseCleared =
      date === null && event.target !== document.activeElement && (event.target as HTMLInputElement).checkValidity();

    // Delay writing to the model in the case where the value is before 1800 to avoid accidentally trying
    // to sync a value from the model that can't be represented in an SQL datetime value.
    if (date && (date.isBefore(minValidDate) || date.isAfter(maxValidDate)) && !submitValues) {
      // In WebKitX, if we use the parsed date value when delaying writing to the model, an issue arises where the year cannot
      // be changed via the keyboard because it clears the value as it is entered. It seems to behave as if the user is entered
      // the first value of the year each time a key is pressed. This doesn't occur if we use the value directly from the input itself.
      setInputValue(event.target.value);
      setDirty(true);
      return;
    }

    // Hack to prevent errors from submitting dates that can't be represented. Should be done server-side really.
    if (submitValues && date && date.isBefore(minValidDate)) {
      date = date.set('year', 1800);
    }
    if (submitValues && date && date.isAfter(maxValidDate)) {
      date = date.set('year', 9999);
    }

    const value = date?.format(formatInfo.html5DateTimeLocalPattern) ?? null;
    setInputValue(value);

    if (date == null && (submitValues || submitValuesBecauseCleared)) {
      //Clear partially entered value to avoid possible confusion with a value being displayed, but not being saved
      event.target.value = '';
    }
    event.data = { name, value, submitValues: submitValues || submitValuesBecauseCleared };
    onChange?.(event);
    setDirty(false);
  };

  const getRef = (instance: HTMLInputElement | null) => {
    // QP-2283 - The clear button in iOS date picker sets the value to the default value.
    // The following is needed to set it to empty string
    // so that it clears - https://github.com/facebook/react/issues/8938
    if (instance) instance.defaultValue = '';
    if (typeof focusRef === 'object' && focusRef) (focusRef as any).current = instance;
  };

  // Undefined needs to pass through to switch between a controlled vs uncontrolled component.
  const inputValueFromModel =
    value === undefined || value === null ? value : moment(value).format(formatInfo.html5DateTimeLocalPattern);

  // Sync value to display from props unless user has made local changes
  // Local changes flagged through handleChange and then synced through handleBlur
  if (value !== undefined && !dirty && inputValue !== inputValueFromModel) {
    setInputValue(inputValueFromModel);
  }

  const cx = classNames('DateTimeInput', { [`Input-${severity}`]: !!severity });
  return (
    <input
      className={cx}
      data-testid={id}
      defaultValue={defaultValue === undefined ? undefined : defaultValue ?? ''}
      disabled={disabled}
      id={id}
      name={name}
      onBlur={handleBlur}
      onChange={handleChange}
      onKeyDown={handleKeyDown}
      ref={getRef}
      type="datetime-local"
      value={value === undefined ? undefined : inputValue ?? ''}
      required={required}
      min={moment('1800-01-01T00:00:00.000').format(formatInfo.html5DateTimeLocalPattern)}
      max={moment('9999-12-31T23:59:59.999').format(formatInfo.html5DateTimeLocalPattern)}
      step={formatInfo.step}
    />
  );
};

const DateTimeInput = withInjectedProps(DateTimeInputBase);

export { DateTimeInput, DateTimeInputBase };
export default DateTimeInput;
